import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { 
  Settings as SettingsIcon, 
  Loader2,
  Save,
  Eye,
  EyeOff,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { FaMicrosoft } from 'react-icons/fa';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { getAuthHeader, user } = useAuth();
  const { theme, setTheme } = useTheme();
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  
  const [testing, setTesting] = useState(false);

  const [formData, setFormData] = useState({
    organization: '',
    personal_access_token: '',
    default_project: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/azure-devops`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
      if (response.data.configured) {
        setFormData({
          organization: response.data.organization || '',
          personal_access_token: '',
          default_project: response.data.default_project || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.organization || !formData.personal_access_token) {
      toast.error('Organisation et PAT sont requis');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/settings/azure-devops`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Paramètres sauvegardés');
      fetchSettings();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.get(`${API_URL}/api/azure-devops/test-connection`, {
        headers: getAuthHeader()
      });
      toast.success(`Connexion réussie ! Organisation: ${response.data.organization}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Impossible de se connecter à Azure DevOps';
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer la configuration Azure DevOps ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/settings/azure-devops`, {
        headers: getAuthHeader()
      });
      toast.success('Configuration supprimée');
      setSettings({ configured: false });
      setFormData({ organization: '', personal_access_token: '', default_project: '' });
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-extrabold">Paramètres</h1>
        <p className="text-muted-foreground mt-1">
          Configurez votre application DevOps Flow
        </p>
      </div>

      {/* Theme Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            Apparence
          </CardTitle>
          <CardDescription>
            Personnalisez l'apparence de l'application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="dark-mode" className="font-medium">Mode sombre</Label>
              <p className="text-sm text-muted-foreground">
                Basculer entre le thème clair et sombre
              </p>
            </div>
            <Switch
              id="dark-mode"
              checked={theme === 'dark'}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              data-testid="theme-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Azure DevOps Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading flex items-center gap-2">
            <FaMicrosoft className="h-5 w-5" />
            Configuration Azure DevOps
          </CardTitle>
          <CardDescription>
            Connectez votre organisation Azure DevOps pour synchroniser vos projets
          </CardDescription>
        </CardHeader>
        <CardContent>
          {settings?.configured && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Configuration active</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Organisation: <span className="font-mono">{settings.organization}</span>
              </p>
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="organization">Organisation Azure DevOps *</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                placeholder="mon-organisation"
                data-testid="settings-organization-input"
              />
              <p className="text-xs text-muted-foreground">
                Le nom de votre organisation (ex: dev.azure.com/<strong>mon-organisation</strong>)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pat">Personal Access Token (PAT) *</Label>
              <div className="relative">
                <Input
                  id="pat"
                  type={showToken ? 'text' : 'password'}
                  value={formData.personal_access_token}
                  onChange={(e) => setFormData({ ...formData, personal_access_token: e.target.value })}
                  placeholder={settings?.configured ? '••••••••••••••••' : 'Entrez votre PAT'}
                  className="pr-10 font-mono"
                  data-testid="settings-pat-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Créez un PAT avec les permissions: Work Items (Read & Write), Code (Read)
              </p>
              <a 
                href="https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Comment créer un PAT ?
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default_project">Projet par défaut (optionnel)</Label>
              <Input
                id="default_project"
                value={formData.default_project}
                onChange={(e) => setFormData({ ...formData, default_project: e.target.value })}
                placeholder="Nom du projet"
                data-testid="settings-default-project-input"
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between gap-2 flex-wrap">
              {settings?.configured && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  data-testid="delete-settings-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer la configuration
                </Button>
              )}
              <div className="flex items-center gap-2 ml-auto">
                {settings?.configured && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testing}
                    className="rounded-sm"
                    data-testid="test-connection-btn"
                  >
                    {testing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    Tester la connexion
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-sm"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Sauvegarder
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">Informations du compte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Nom</span>
              <span className="font-medium">{user?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rôle</span>
              <span className="font-medium capitalize">{user?.role}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auth info */}
      <Card className="bg-muted/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-sm shrink-0">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-heading font-semibold mb-1">Authentification Azure Entra ID</h3>
              <p className="text-sm text-muted-foreground">
                Vous êtes connecté via votre compte Microsoft professionnel (Azure Entra ID).
                Votre compte DevOps Flow est automatiquement lié à votre identité Microsoft.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
